import { useState } from 'react';
import styled from '@emotion/styled';
import { Copy, Check } from 'lucide-react';
import { Card } from '@template-project/afrodita-components';
import type { Order } from '@/domain/entities/order.entity';
import { OrderStatusBadge } from './OrderStatusBadge';
import { formatOrderDate, formatCurrency, formatAddress, getOrderTotalQuantity } from './utils';

const StyledCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.space.lg};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space.md};
  padding-bottom: ${({ theme }) => theme.space.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
  margin-bottom: ${({ theme }) => theme.space.lg};
`;

const OrderNumberLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: inline-flex;
  align-items: center;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const HeaderDate = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-left: auto;
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: ${({ theme }) => theme.space.xl};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    grid-template-columns: auto 1fr;
  }
`;

const ThumbnailGroup = styled.div`
  display: flex;
  gap: 4px;
`;

const Thumbnail = styled.img`
  width: 60px;
  height: 75px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.small};
  background: ${({ theme }) => theme.colors.background.secondary};
`;

const ItemCountLabel = styled.span`
  display: block;
  margin-top: 4px;
  font-size: ${({ theme }) => theme.fontSizes[0]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.space.lg};

  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.space.sm};
  }
`;

const SummaryItem = styled.div`
  h4 {
    font-size: ${({ theme }) => theme.fontSizes[1]}px;
    font-weight: ${({ theme }) => theme.typography.config.weight.bold};
    color: ${({ theme }) => theme.colors.text.secondary};
    margin: 0 0 4px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  p {
    font-size: ${({ theme }) => theme.fontSizes[2]}px;
    color: ${({ theme }) => theme.colors.text.primary};
    margin: 0;
  }
`;

const ViewButton = styled.button`
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.xl};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  letter-spacing: 0.5px;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.text.primary};
    color: ${({ theme }) => theme.colors.background.primary};
  }

  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    grid-column: 1 / -1;
    justify-self: start;
    margin-top: ${({ theme }) => theme.space.md};
  }
`;

const MAX_THUMBNAILS = 3;

interface OrderCardProps {
  readonly order: Order;
  readonly locale?: string;
  readonly currencyCode?: string;
  readonly currencySymbol?: string;
  readonly onViewDetails: (orderId: string) => void;
}

export function OrderCard({
  order,
  locale = 'en-US',
  currencyCode = 'USD',
  currencySymbol,
  onViewDetails,
}: OrderCardProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(order.number);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const thumbItems = order.line_items?.slice(0, MAX_THUMBNAILS) ?? [];
  const totalQty = getOrderTotalQuantity(order);

  return (
    <StyledCard>
      <Header>
        <OrderNumberLabel>
          Order #{order.number}
          <CopyButton onClick={handleCopy} aria-label="Copy order number">
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </CopyButton>
        </OrderNumberLabel>
        <OrderStatusBadge order={order} />
        <HeaderDate>{formatOrderDate(order.purchase_date)}</HeaderDate>
      </Header>

      <Body>
        <div>
          <ThumbnailGroup>
            {thumbItems.map((item) => (
              <Thumbnail
                key={item.id}
                src={item.image_url || 'https://placehold.co/60x75/f5f5f5/999?text=N/A'}
                alt={item.name}
                loading="lazy"
              />
            ))}
          </ThumbnailGroup>
          <ItemCountLabel>
            {totalQty} {totalQty === 1 ? 'item' : 'items'}
          </ItemCountLabel>
        </div>

        <SummaryGrid>
          <SummaryItem>
            <h4>Order Placed</h4>
            <p>{formatOrderDate(order.purchase_date)}</p>
          </SummaryItem>
          <SummaryItem>
            <h4>Ship To</h4>
            <p>{formatAddress(order.shipping_address)}</p>
          </SummaryItem>
          <SummaryItem>
            <h4>Total</h4>
            <p>{formatCurrency(order.totals.total.display, locale, currencyCode, currencySymbol)}</p>
          </SummaryItem>
        </SummaryGrid>

        <ViewButton onClick={() => onViewDetails(order.id)}>VIEW DETAILS</ViewButton>
      </Body>
    </StyledCard>
  );
}
